// ============================================================
// CaseActivitySection.tsx
// Stage 4 (approved mockup casevo-case-sections-batch3.html §2): the case
// page's ACTIVITY section — the billable timeline. It mounts the SAME
// ClientActivityFeed the client page uses (cards / ledger remembered per
// browser, entry-type registry, KPIs, family filters with "Soon"
// placeholders, drafts strip, date groups + subtotals, billing rules),
// SCOPED TO THIS CASE:
//   • activities: listActivitiesApi({ clientId, caseId, …filters }) — the
//     case filter is fixed (the feed shows a locked "This case" chip)
//   • documents: this case's files (fetchDocumentSearchApi({ caseId })),
//     shown at no charge, never "£0.00"
//   • new entries are created on this case (AddActivityModal defaultCaseId,
//     ComposeEmailModal caseId); the per-entry case chip is hidden
// Every handler mirrors ClientSearchView's Activity wiring 1:1 (load /
// filter refetch with the dim "Updating…" flag / create / edit / view /
// delete-confirm / drafts / compose / document open-download-delete / live
// refetch on another user's activity change). Billing visibility is
// server-side (grade + rate admin-only, colleagues' £ withheld as null →
// lock + "(yours)"), billed entries are locked — all inside the feed,
// unchanged. History stays the separate read-only audit trail.
// ============================================================
import { refusalText } from '../../utils/serverError'
import { useLivePermissions } from '../../hooks/useLivePermissions'
import React, { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import {
  listActivitiesApi, deleteActivityApi, fetchDocumentSearchApi, requestDownloadUrlFlatApi, deleteFileFlatApi,
  listEmailDraftsApi, deleteEmailDraftApi, listFilesApi, type EmailDraft,
} from '../../api/casevoApi'
import type { Activity } from '../../types/activity.types'
import type { DocumentSearchResult } from '../../types/documentSearch.types'
import type { ActionTypeValue } from '../../constants/actionTypes.constants'
import { entryTypeFor } from '../../constants/entryTypes'
import { presence } from '../../services/presence'
import { useCaseChange } from '../../utils/caseChangeEvents'
import { isInlineSafeMime, openUrlInTab, triggerBlobDownload } from '../../utils/documentAccess'
import { caseName } from '../../utils/caseName'
import { getCaseReference } from '../../utils/caseHelpers'
import ClientActivityFeed, { type ActivityCaseOption } from '../clients/ClientActivityFeed'
import AddActivityModal, { type ActionVariantKey } from '../shared/AddActivityModal'
import ComposeEmailModal from '../shared/ComposeEmailModal'
import ActivityViewModal from '../shared/ActivityViewModal'
import ConfirmDialogue from '../shared/ConfirmDialogue'
import { CASE_CARD } from '../case/caseUi'
import { useBillEvents } from '../../hooks/useBillEvents'

type ActivityTypeFilter = 'all' | ActionTypeValue

// ── Unbilled WIP per case (the nav row's "£253") ─────────────
// A tiny external store: the section publishes after every load; the
// ViewingPane nav reads it (and seeds it once per case via useCaseActivityWip).
const wipByCase = new Map<string, number>()
const wipListeners = new Set<() => void>()
const publishWip = (caseId: string, activities: Activity[]): void => {
  let pence = 0
  for (const a of activities) {
    if (!entryTypeFor(a.actionType).bills || a.billed) continue
    pence += a.amountPence ?? 0 // withheld colleagues' £ (null) never counted
  }
  if (wipByCase.get(caseId) === pence) return
  wipByCase.set(caseId, pence)
  wipListeners.forEach((l) => l())
}
const seededWip = new Set<string>()
// ★ (Audit) These caches are module-level and outlive a sign-out (logout
// doesn't reload the page) — so a second user signing in on the same tab saw
// the previous user's £ WIP figure (possibly one they lack "See colleagues'
// billing" for). Clear them whenever the signed-in user changes.
let wipUserId: string | null = useCasevoStore.getState().authUser?.id ?? null
useCasevoStore.subscribe((st) => {
  const uid = st.authUser?.id ?? null
  if (uid === wipUserId) return
  wipUserId = uid
  wipByCase.clear()
  seededWip.clear()
  wipListeners.forEach((l) => l())
})
// Nav WIP for a case: seeds with one scoped fetch the first time the case is
// shown (if the Activity section hasn't already published), refreshes on a
// colleague's activity change for this case. null = not known yet.
export const useCaseActivityWip = (caseId: string | null, clientId: string | null, enabled: boolean): number | null => {
  const value = useSyncExternalStore(
    (cb) => { wipListeners.add(cb); return () => { wipListeners.delete(cb) } },
    () => (caseId ? wipByCase.get(caseId) ?? null : null),
  )
  useEffect(() => {
    if (!enabled || !caseId || !clientId) return
    const load = (): void => {
      void listActivitiesApi({ clientId, caseId }).then(({ activities }) => publishWip(caseId, activities)).catch(() => undefined)
    }
    if (!seededWip.has(caseId)) { seededWip.add(caseId); if (!wipByCase.has(caseId)) load() }
    let timer: ReturnType<typeof setTimeout> | null = null
    const unsub = presence.subscribeEntityChanged((e) => {
      if (e.entity !== 'activity' || e.scope?.clientId !== clientId) return
      if (e.scope?.caseId && e.scope.caseId !== caseId) return
      if (timer) clearTimeout(timer)
      timer = setTimeout(load, 600)
    })
    return () => { unsub(); if (timer) clearTimeout(timer) }
  }, [caseId, clientId, enabled])
  // (D12) Someone else's time / activity / billing change on this case.
  useCaseChange(enabled && clientId ? caseId : null, ['activity', 'bill'], (_k, info) => {
    if (!info.remote || !caseId || !clientId) return
    void listActivitiesApi({ clientId, caseId }).then(({ activities }) => publishWip(caseId, activities)).catch(() => undefined)
  })
  return value
}

const CaseActivitySection: React.FC = () => {
  const currentCase = useCasevoStore((s) => s.currentCase)
  const authUser = useCasevoStore((s) => s.authUser)
  const setFiles = useCasevoStore((s) => s.setFiles)
  const client = currentCase?.client ?? null
  const caseId = currentCase?.id ?? ''
  const clientId = client?.id ?? null

  const [activities, setActivities] = useState<Activity[] | null>(null)
  const [activitiesAll, setActivitiesAll] = useState<Activity[] | null>(null)
  const [activityError, setActivityError] = useState<string | null>(null)
  const [documents, setDocuments] = useState<DocumentSearchResult[] | null>(null)
  const [documentsError, setDocumentsError] = useState<string | null>(null)
  // Billing on the timeline — this case's issued bills, payments, credit notes (masked server-side).
  const { events: billEvents, error: billEventsError } = useBillEvents(caseId ? { caseId } : null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [refetching, setRefetching] = useState(false)
  const [typeFilter, setTypeFilter] = useState<ActivityTypeFilter>('all')
  const [corrFilter, setCorrFilter] = useState<string | null>(null)
  const [drafts, setDrafts] = useState<EmailDraft[]>([])
  // Which client|case the drafts were last loaded for — until it matches, the
  // feed treats drafts as still loading (no strip popping in later).
  const [draftsKey, setDraftsKey] = useState<string | null>(null)
  const [resumeDraft, setResumeDraft] = useState<EmailDraft | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [variant, setVariant] = useState<ActionVariantKey | null>(null)
  const [editing, setEditing] = useState<Activity | null>(null)
  const [viewing, setViewing] = useState<Activity | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Activity | null>(null)
  const [deletingActivityId, setDeletingActivityId] = useState<string | null>(null)
  const [showCompose, setShowCompose] = useState(false)
  const [emailToast, setEmailToast] = useState<string | null>(null)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null)
  const [confirmDeleteDoc, setConfirmDeleteDoc] = useState<DocumentSearchResult | null>(null)

  // Drop responses for a case we've since left (same guard as the client page).
  const activeKeyRef = useRef(caseId)
  activeKeyRef.current = caseId

  // ── loaders (ClientSearchView loadActivitiesForClient / chip source / docs / drafts) ──
  const loadActivities = useCallback(async (filters?: { actionType?: string; correspondentType?: string }): Promise<void> => {
    if (!clientId) return
    const at = caseId
    try {
      const { activities: rows } = await listActivitiesApi({
        clientId,
        caseId: at,
        ...(filters?.actionType ? { actionType: filters.actionType } : {}),
        // '' is meaningful (untagged) — thread on `!== undefined`, not truthiness.
        ...(filters?.correspondentType !== undefined ? { correspondentType: filters.correspondentType } : {}),
      })
      if (activeKeyRef.current !== at) return
      setActivities(rows)
      setActivityError(null)
    } catch (err) {
      if (activeKeyRef.current !== at) return
      setActivityError(err instanceof Error ? err.message : 'Failed to load activity for this case')
    }
  }, [clientId, caseId])
  const loadAll = useCallback(async (): Promise<void> => {
    if (!clientId) return
    const at = caseId
    try {
      const { activities: rows } = await listActivitiesApi({ clientId, caseId: at })
      if (activeKeyRef.current !== at) return
      setActivitiesAll(rows)
      publishWip(at, rows)
    } catch { /* non-fatal — KPIs fall back to the filtered list */ }
  }, [clientId, caseId])
  const loadDocuments = useCallback(async (): Promise<void> => {
    const at = caseId
    try {
      const rows = await fetchDocumentSearchApi({ caseId: at })
      if (activeKeyRef.current !== at) return
      setDocuments(rows)
      setDocumentsError(null)
    } catch (err) {
      if (activeKeyRef.current !== at) return
      setDocumentsError(err instanceof Error ? err.message : 'Failed to load documents for this case')
      setDocuments([])
    }
  }, [caseId])
  const loadDrafts = useCallback(async (): Promise<void> => {
    if (!clientId) { setDrafts([]); setDraftsKey(`${clientId}|${caseId}`); return }
    try {
      const rows = await listEmailDraftsApi({ clientId })
      // This case's drafts (a draft started on the client page with no case stays there).
      setDrafts(rows.filter((d) => d.caseId === caseId))
    } catch {
      setDrafts([])
    } finally {
      setDraftsKey(`${clientId}|${caseId}`)
    }
  }, [clientId, caseId])

  const currentFilters = (): { actionType?: string; correspondentType?: string } => ({
    actionType: typeFilter === 'all' ? undefined : typeFilter,
    correspondentType: corrFilter === null ? undefined : corrFilter,
  })

  // Initial load per case (resets like a client switch).
  useEffect(() => {
    setActivities(null); setActivitiesAll(null); setDocuments(null)
    setActivityError(null); setDocumentsError(null); setActionError(null)
    setTypeFilter('all'); setCorrFilter(null)
    if (!caseId) return
    if (!clientId) { setActivities([]); setActivitiesAll([]) } else { void loadActivities(); void loadAll() }
    void loadDocuments()
    void loadDrafts()
  }, [caseId, clientId, loadActivities, loadAll, loadDocuments, loadDrafts])

  // ── live freshness: another user's activity create/update/delete on this
  // case → the same scoped refetch (debounced), with the CURRENT filters. A
  // socket RE-connect does the same (events sent while it was down are lost —
  // as the billing screens do, useBillingLive).
  const liveFiltersRef = useRef(currentFilters())
  liveFiltersRef.current = currentFilters()
  useEffect(() => {
    if (!clientId) return
    let timer: ReturnType<typeof setTimeout> | null = null
    const scheduleRefetch = (): void => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        timer = null
        void loadActivities(liveFiltersRef.current)
        void loadAll()
      }, 400)
    }
    const offs = [
      presence.subscribeEntityChanged((e) => {
        if (e.entity !== 'activity' || (e.op !== 'create' && e.op !== 'delete' && e.op !== 'update')) return
        if (e.scope?.clientId !== clientId) return
        if (e.scope?.caseId && e.scope.caseId !== caseId) return
        scheduleRefetch()
      }),
      presence.subscribeReconnected(scheduleRefetch),
    ]
    return () => { offs.forEach((off) => off()); if (timer) clearTimeout(timer) }
  }, [clientId, caseId, loadActivities, loadAll])

  // (D12 live updates) Someone else's change on this case (incl. the actor's
  // other tabs, which entity-changed skips): activity / correspondence /
  // drafts, and the documents shown on the timeline.
  useCaseChange(caseId, ['activity', 'documents'], (_k, info) => {
    if (!info.remote) return
    if (info.kinds.includes('activity') && clientId) { void loadActivities(liveFiltersRef.current); void loadAll(); void loadDrafts() }
    if (info.kinds.includes('documents')) void loadDocuments()
  })

  // ── filter handlers (server-side refetch + dim "Updating…") ──
  const onTypeFilter = (next: ActivityTypeFilter): void => {
    setTypeFilter(next)
    setRefetching(true)
    void loadActivities({ actionType: next === 'all' ? undefined : next, correspondentType: corrFilter === null ? undefined : corrFilter })
      .finally(() => setRefetching(false))
  }
  const onCorrFilter = (next: string | null): void => {
    setCorrFilter(next)
    setRefetching(true)
    void loadActivities({ actionType: typeFilter === 'all' ? undefined : typeFilter, correspondentType: next === null ? undefined : next })
      .finally(() => setRefetching(false))
  }
  const onClearServerFilters = (): void => {
    setTypeFilter('all'); setCorrFilter(null)
    setRefetching(true)
    void loadActivities().finally(() => setRefetching(false))
  }
  const afterMutation = (): void => { void loadActivities(currentFilters()); void loadAll() }
  // ★ Live permission change: lost a money right → drop the £ on screen now,
  // then re-read under the new rights (the server redacts).
  useLivePermissions((narrowed) => { if (narrowed) { setActivities([]); setActivitiesAll(null); setViewing(null) } afterMutation() })

  const handleConfirmDeleteActivity = async (): Promise<void> => {
    if (!confirmDelete) return
    setDeletingActivityId(confirmDelete.id)
    try {
      await deleteActivityApi(confirmDelete.id)
      setConfirmDelete(null)
      await loadActivities(currentFilters())
      void loadAll()
    } catch (err) {
      // Closed first: the reason shows on the list, not behind the dialog.
      setConfirmDelete(null)
      setActivityError(err instanceof Error ? err.message : 'Failed to delete activity')
    } finally {
      setDeletingActivityId(null)
    }
  }

  // ── documents (ClientSearchView handleOpen/Download/ConfirmDelete) ──
  const handleOpenDocument = async (d: DocumentSearchResult): Promise<void> => {
    setActionError(null)
    setDownloadingId(d.id)
    try {
      const { downloadUrl, filename, mimeType } = await requestDownloadUrlFlatApi(d.id, true)
      if (isInlineSafeMime(mimeType)) openUrlInTab(downloadUrl)
      else await triggerBlobDownload(downloadUrl, filename)
    } catch {
      setActionError('Failed to open file. Please try again.')
    } finally {
      setDownloadingId(null)
    }
  }
  const handleDownloadDocument = async (d: DocumentSearchResult): Promise<void> => {
    setActionError(null)
    setDownloadingId(d.id)
    try {
      const { downloadUrl, filename } = await requestDownloadUrlFlatApi(d.id)
      await triggerBlobDownload(downloadUrl, filename)
    } catch {
      setActionError('Failed to download file. Please try again.')
    } finally {
      setDownloadingId(null)
    }
  }
  const handleConfirmDeleteDoc = async (): Promise<void> => {
    const doc = confirmDeleteDoc
    if (!doc) return
    setConfirmDeleteDoc(null)
    setDeletingDocId(doc.id)
    const previous = documents
    setDocuments((prev) => prev?.filter((x) => x.id !== doc.id) ?? prev)
    try {
      await deleteFileFlatApi(doc.id)
      // Keep the Documents section's list (store files slice) in step.
      void listFilesApi(caseId).then((rows) => setFiles(caseId, rows)).catch(() => undefined)
    } catch (err) {
      setDocuments(previous)
      setActionError(err instanceof Error ? err.message : 'Failed to delete document. Please try again.')
    } finally {
      setDeletingDocId(null)
    }
  }

  const caseOption = useMemo<ActivityCaseOption[]>(() => (currentCase ? [{
    id: currentCase.id,
    name: caseName({ caseName: currentCase.caseName, clientForename: currentCase.clientDetails.forename, clientSurname: currentCase.clientDetails.surname, client: currentCase.client, defendantName: currentCase.defendant?.name }),
    ref: getCaseReference(currentCase),
  }] : []), [currentCase])
  // AddActivityModal's case dropdown rows (ClientCaseRow shape).
  const caseRows = useMemo(() => (currentCase ? [{
    id: currentCase.id,
    accidentDate: currentCase.accidentDetails.accidentDate || null,
    closeDate: currentCase.caseControl.closeDate || null,
    setupFinalised: currentCase.caseControl.setupFinalised,
    updatedAt: currentCase.updatedAt,
    ref: caseOption[0]?.ref,
    name: caseOption[0]?.name,
  }] : []), [currentCase, caseOption])

  if (!currentCase) return null
  if (!client) {
    // Activities hang off a client (every case created post-Phase 1.4 has one).
    return (
      <div className={`${CASE_CARD} px-6 py-10 text-center text-[13.5px] text-casevo-text-soft`}>
        This case isn't linked to a client yet, so billable activity can't be recorded here.
      </div>
    )
  }
  const clientDisplay = [client.title, client.forename, client.surname].filter(Boolean).join(' ')
  const canModify = (a: Activity): boolean => authUser !== null && (authUser.role === 'admin' || authUser.id === a.actorUserId)

  return (
    <div className="text-[14px] leading-[1.5] text-casevo-forest">
      <ClientActivityFeed
        key={caseId}
        caseScope={{ caseId }}
        clientFirstName={client.forename ?? ''}
        activities={activities}
        activitiesAll={activitiesAll}
        documents={documents}
        billEvents={billEvents}
        refetching={refetching}
        errors={[activityError, documentsError, billEventsError, actionError]}
        isAdmin={authUser?.role === 'admin'}
        currentUserId={authUser?.id ?? null}
        cases={caseOption}
        typeFilter={typeFilter}
        onTypeFilter={(v) => onTypeFilter(v as ActivityTypeFilter)}
        caseFilter={caseId}
        onCaseFilter={() => { /* fixed to this case */ }}
        corrFilter={corrFilter}
        onCorrFilter={onCorrFilter}
        onClearServerFilters={onClearServerFilters}
        drafts={drafts}
        draftsLoading={draftsKey !== `${clientId}|${caseId}`}
        onResumeDraft={(d) => { setResumeDraft(d); setShowCompose(true) }}
        onDeleteDraft={(d) => {
          const prev = drafts
          setDrafts((rows) => rows.filter((r) => r.id !== d.id))
          void deleteEmailDraftApi(d.id).catch((err) => {
            setDrafts(prev)
            setEmailToast(refusalText(err, 'Could not delete the draft.'))
            window.setTimeout(() => setEmailToast(null), 3000)
          })
        }}
        canModify={canModify}
        deletingActivityId={deletingActivityId}
        downloadingDocId={downloadingId}
        deletingDocId={deletingDocId}
        onViewActivity={(a) => setViewing(a)}
        onEditActivity={(a) => setEditing(a)}
        onDeleteActivity={(a) => setConfirmDelete(a)}
        onOpenDocument={(d) => { void handleOpenDocument(d) }}
        onDownloadDocument={(d) => { void handleDownloadDocument(d) }}
        onDeleteDocument={(d) => { setActionError(null); setConfirmDeleteDoc(d) }}
        onOpenCase={() => { /* already on this case */ }}
        onNewActivity={() => { setVariant(null); setShowAdd(true) }}
        onLogVariant={(v) => { setVariant(v); setShowAdd(true) }}
        // The ONE "Invoice" action → this case's Billing section, straight into the
        // real Create bill flow (the same RaiseBillModal the Billing tab uses).
        onCreateInvoice={() => {
          const s = useCasevoStore.getState()
          s.setPendingRaiseBillCaseId(caseId)
          // Billing is another section of this case — a history step.
          s.pushViewHistory()
          s.setActiveCaseSection('billing')
        }}
        onSendEmail={() => setShowCompose(true)}
        onUpload={() => { /* uploads live in the Documents section */ }}
      />

      <ComposeEmailModal
        isOpen={showCompose}
        onClose={() => { setShowCompose(false); setResumeDraft(null) }}
        clientId={client.id}
        clientDisplayName={clientDisplay}
        caseId={caseId}
        caseRefLabel={caseOption[0]?.ref ?? null}
        defaultTo={client.emailAddress || null}
        resumeDraft={resumeDraft}
        onDraftSaved={() => {
          void loadDrafts()
          setEmailToast('Draft saved.')
          window.setTimeout(() => setEmailToast(null), 3000)
        }}
        onSent={() => {
          afterMutation()
          void loadDrafts()
          setEmailToast('Email sent.')
          window.setTimeout(() => setEmailToast(null), 3000)
        }}
      />
      {emailToast && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-6 z-[220] rounded-full border border-casevo-border bg-casevo-surface shadow-lg px-4 py-2 text-xs text-casevo-forest">
          {emailToast}
        </div>
      )}
      <AddActivityModal
        isOpen={showAdd}
        onClose={() => { setShowAdd(false); setVariant(null) }}
        clientId={client.id}
        clientDisplayName={clientDisplay}
        cases={caseRows}
        defaultCaseId={caseId}
        onCreated={afterMutation}
        variant={variant}
        clientPhone={client.mainContactNumber ?? null}
        onPickEmail={() => { setShowAdd(false); setVariant(null); setShowCompose(true) }}
      />
      {editing && (
        <AddActivityModal
          isOpen={true}
          onClose={() => setEditing(null)}
          clientId={client.id}
          clientDisplayName={clientDisplay}
          cases={caseRows}
          existingActivity={editing}
          onCreated={() => { setEditing(null); afterMutation() }}
        />
      )}
      <ActivityViewModal
        isOpen={viewing !== null}
        activity={viewing}
        onClose={() => setViewing(null)}
        isAdmin={authUser?.role === 'admin'}
        canModify={!!viewing && canModify(viewing)}
        onEdit={(a) => { setViewing(null); setEditing(a) }}
        onDelete={(a) => { setViewing(null); setConfirmDelete(a) }}
        onRepriced={() => { setViewing(null); afterMutation() }}
        caseLabel={viewing?.caseId ? (caseOption.find((c) => c.id === viewing.caseId) ?? null) : null}
      />
      <ConfirmDialogue
        isOpen={confirmDelete !== null}
        title="Delete activity"
        message={confirmDelete ? `Delete this activity entry (${confirmDelete.action})? This can't be undone.` : ''}
        confirmLabel={deletingActivityId ? 'Deleting...' : 'Delete'}
        onConfirm={() => { void handleConfirmDeleteActivity() }}
        onCancel={() => setConfirmDelete(null)}
      />
      <ConfirmDialogue
        isOpen={confirmDeleteDoc !== null}
        title="Delete document?"
        message={confirmDeleteDoc ? `${confirmDeleteDoc.filename} will be permanently deleted from this case. This cannot be undone.` : ''}
        confirmLabel={deletingDocId ? 'Deleting...' : 'Delete'}
        onConfirm={() => { void handleConfirmDeleteDoc() }}
        onCancel={() => setConfirmDeleteDoc(null)}
      />
    </div>
  )
}

export default CaseActivitySection
